// An item's status as the platform's status dropdown (DD-062, finance-real, the user 2026-10-22): wherever a job, a quote
// or an invoice shows its status, on a row or in its drawer, it is this pill, with each status's colours as
// IdeaStatusBadge has it, never a plain badge beside an action button. A status another record sets shows in the menu,
// disabled, naming what sets it ("By the quote", "By the invoice"), the current one too, so an Invoiced job's menu says
// what invoiced it; the pill itself shows the label alone. One the server would refuse from where the item stands is
// disabled too. A status shown as a reference to another record (the job drawer's quote and invoice rows, the client and
// person drawers' invoice rows, To do's rows, a job picker) is StatusPill alone, everywhere, named by `ariaLabel`: no
// chevron, no menu. JobStatusBadge, QuoteStatusBadge and InvoiceStatusBadge say which statuses each
// takes. Built on DropdownMenu, not OptionDropdown, whose pill would repeat the current status's tag.
import { ChevronDownIcon, SpinnerIcon } from "@/components/icons";
import { DropdownMenu, type DropdownMenuEntry } from "@/components/ui/DropdownMenu";
import { cn } from "@/utils/cn";
import type { StatusLook } from "./lineJobText";

export interface StatusChoice<T extends string> {
  value: T;
  look: StatusLook;
  /** What sets it, when the user does not: shown as its tag, and it is disabled. */
  setBy?: string;
  /** Not open from where the item stands. */
  disabled?: boolean;
  /** Why a disabled choice is closed, shown as its tag: every disabled choice but the current one says why. */
  why?: string;
}

/** The pill alone: the one read-only status, a status shown as a reference, which another record changes. With
 * `ariaLabel` it is a status of its own name ("Invoice INV2600048 status"); inside a row whose name says it, none. */
export function StatusPill({ look, loading = false, chevron = false, ariaLabel }: { look: StatusLook; loading?: boolean; chevron?: boolean; ariaLabel?: string }) {
  return (
    <span
      role={ariaLabel ? "status" : undefined}
      aria-label={ariaLabel}
      className={cn("inline-flex shrink-0 items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap", look.bg, look.text)}
    >
      {loading ? <SpinnerIcon className="w-3 h-3 animate-spin" /> : <span className={cn("w-1.5 h-1.5 rounded-full", look.dot)} />}
      {look.label}
      {chevron && <ChevronDownIcon className="w-3 h-3 opacity-60" />}
    </span>
  );
}

interface Props<T extends string> {
  value: T;
  choices: StatusChoice<T>[];
  /** Absent: read-only, the pill with no menu. */
  onPick?: (status: T) => void;
  /** Names the trigger: "<item> status". */
  ariaLabel: string;
  loading?: boolean;
  align?: "left" | "right";
}

export function StatusBadge<T extends string>({ value, choices, onPick, ariaLabel, loading = false, align = "left" }: Props<T>) {
  const current = choices.find((c) => c.value === value);
  if (!current) return null;
  if (!onPick) return <StatusPill look={current.look} ariaLabel={ariaLabel} />;
  const items: DropdownMenuEntry[] = choices.map((c) => ({
    id: c.value,
    label: c.look.label,
    badge: c.setBy ?? (c.disabled ? c.why : undefined),
    icon: <span className={cn("w-2 h-2 rounded-full", c.look.dot)} />,
    onClick: () => onPick(c.value),
    disabled: loading || c.value === value || c.setBy !== undefined || c.disabled === true,
  }));
  return (
    <DropdownMenu
      items={items}
      width="w-64"
      align={align}
      menuClassName="rounded-xl"
      trigger={
        <button type="button" aria-label={ariaLabel} className={cn("inline-flex transition-colors", loading && "opacity-70")}>
          <StatusPill look={current.look} loading={loading} chevron />
        </button>
      }
    />
  );
}
